import { addMoney, type Debt, type DebtTotals, sumByCurrency } from '@familywallet/shared';

/** What is still owed on these debts: per currency (never mixed), plus the ≈ base estimate. */
export function debtTotals(debts: readonly Debt[], baseCurrency: string): DebtTotals {
  return {
    byCurrency: sumByCurrency(
      debts.map((d) => ({ currency: d.currency, amount: d.outstanding })),
      baseCurrency,
    ),
    inBase: {
      currency: baseCurrency,
      amount: addMoney('0', ...debts.map((d) => d.outstandingInBase.amount)),
      estimated: debts.some((d) => d.outstandingInBase.estimated),
      missingRates: [...new Set(debts.flatMap((d) => d.outstandingInBase.missingRates))].sort(),
    },
  };
}

/** One person's open debts in a section, with their total. */
export interface PersonGroup {
  key: string;
  name: string;
  debts: Debt[];
  totals: DebtTotals;
}

export interface DebtSection {
  totals: DebtTotals;
  people: PersonGroup[];
}

const personKey = (d: Debt) =>
  d.person.type === 'outside' ? `p:${d.person.personId}` : `m:${d.person.memberId}`;

/** Debts grouped by the other person, in the order they first appear (newest first). */
export function debtSection(debts: readonly Debt[], baseCurrency: string): DebtSection {
  const groups = new Map<string, Debt[]>();
  for (const d of debts) {
    const key = personKey(d);
    groups.set(key, [...(groups.get(key) ?? []), d]);
  }
  return {
    totals: debtTotals(debts, baseCurrency),
    people: [...groups].map(([key, items]) => ({
      key,
      name: items[0]!.person.name,
      debts: items,
      totals: debtTotals(items, baseCurrency),
    })),
  };
}

/**
 * The open debts with people outside the family, split the way people talk about them: cash
 * loans ("Borrowed" / "Lent") apart from things bought or sold on credit. The list holds only
 * the debts the viewer can see, so every total here is a total of what they can see.
 */
export function outsideSections(debts: readonly Debt[], baseCurrency: string) {
  const open = debts.filter((d) => !d.settled && !d.internal);
  const pick = (direction: Debt['direction'], credit: boolean) =>
    debtSection(
      open.filter((d) => d.direction === direction && (d.credit !== null) === credit),
      baseCurrency,
    );
  return {
    borrowed: pick('borrowed', false),
    boughtOnCredit: pick('borrowed', true),
    lent: pick('lent', false),
    soldOnCredit: pick('lent', true),
  };
}
